import { useState, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast, Toaster } from "sonner";
import { CheckCircle, Paperclip, X, Translate } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Full form translations
const I = {
  en: {
    title: "Client intake",
    subtitle: "Tell us about your interpretation or translation needs — takes about 3 minutes.",
    langLabel: "Form language",
    section_basic: "Your information",
    name: "Organization / your name", contact: "Contact person", email: "Email", phone: "Phone", address: "Mailing address",
    pref_lang: "Preferred language for communication",
    section_service: "Service needed",
    service_type: "Which service do you need?",
    interpretation: "Interpretation (in-person / phone / video)",
    translation: "Translation (documents)",
    both: "Both interpretation and translation",
    org_type: "Case / matter type",
    section_interp: "Interpretation details", int_langs: "Language(s) you need interpreted",
    int_format: "Preferred format", int_first_date: "Expected first assignment (optional)",
    format_in_person: "In person", format_opi: "OPI phone", format_vri: "VRI video",
    section_transl: "Translation details", src_lang: "Source language", tgt_lang: "Target language",
    doc_type: "Document type",
    doc_certificate: "Certificate", doc_medical: "Medical", doc_legal: "Legal", doc_business: "Business", doc_other: "Other",
    volume: "Approx. size (e.g. 1500 words or 12 pages)",
    section_billing: "Billing", billing_contact: "Billing contact (if different)",
    billing_method: "Preferred billing method", bm_invoice: "Invoice (Net-15)", bm_card: "Credit card", bm_po: "Purchase order",
    po_number: "PO number",
    section_files: "Upload files (optional)",
    files_hint: "Attach up to 5 documents (max 25 MB total) — source documents, ID, court orders, etc.",
    remove: "Remove",
    section_notes: "Anything else?", notes_placeholder: "Anything we should know…",
    submit: "Submit intake", submitting: "Submitting…",
    success_title: "Thank you!", success_body: "Your intake has been received. Our team will review it and reach out shortly.",
    required: "required", any: "Any",
  },
  es: {
    title: "Formulario de inscripción",
    subtitle: "Cuéntenos sobre sus necesidades de interpretación o traducción — toma unos 3 minutos.",
    langLabel: "Idioma del formulario",
    section_basic: "Su información",
    name: "Organización / su nombre", contact: "Persona de contacto", email: "Correo electrónico", phone: "Teléfono", address: "Dirección postal",
    pref_lang: "Idioma preferido para comunicación",
    section_service: "Servicio solicitado",
    service_type: "¿Qué servicio necesita?",
    interpretation: "Interpretación (presencial / teléfono / video)",
    translation: "Traducción (documentos)",
    both: "Ambos: interpretación y traducción",
    org_type: "Tipo de caso / asunto",
    section_interp: "Detalles de interpretación", int_langs: "Idioma(s) para interpretación",
    int_format: "Formato preferido", int_first_date: "Fecha aproximada del primer servicio (opcional)",
    format_in_person: "Presencial", format_opi: "Teléfono OPI", format_vri: "Video VRI",
    section_transl: "Detalles de traducción", src_lang: "Idioma de origen", tgt_lang: "Idioma de destino",
    doc_type: "Tipo de documento",
    doc_certificate: "Certificado", doc_medical: "Médico", doc_legal: "Legal", doc_business: "Comercial", doc_other: "Otro",
    volume: "Tamaño aproximado (p. ej. 1500 palabras o 12 páginas)",
    section_billing: "Facturación", billing_contact: "Contacto de facturación (si es diferente)",
    billing_method: "Método de facturación preferido", bm_invoice: "Factura (Net-15)", bm_card: "Tarjeta de crédito", bm_po: "Orden de compra",
    po_number: "Número de PO",
    section_files: "Cargar archivos (opcional)",
    files_hint: "Adjunte hasta 5 documentos (máx. 25 MB) — documentos originales, ID, órdenes judiciales, etc.",
    remove: "Eliminar",
    section_notes: "¿Algo más?", notes_placeholder: "Algo que debamos saber…",
    submit: "Enviar", submitting: "Enviando…",
    success_title: "¡Gracias!", success_body: "Hemos recibido su formulario. Nuestro equipo lo revisará y le contactará pronto.",
    required: "requerido", any: "Cualquiera",
  },
  fr: {
    title: "Formulaire d'inscription client",
    subtitle: "Parlez-nous de vos besoins en interprétation ou traduction — environ 3 minutes.",
    langLabel: "Langue du formulaire",
    section_basic: "Vos informations",
    name: "Organisation / votre nom", contact: "Personne à contacter", email: "Courriel", phone: "Téléphone", address: "Adresse postale",
    pref_lang: "Langue préférée pour la communication",
    section_service: "Service demandé",
    service_type: "Quel service vous faut-il ?",
    interpretation: "Interprétation (en personne / téléphone / vidéo)",
    translation: "Traduction (documents)",
    both: "Interprétation et traduction",
    org_type: "Type de dossier",
    section_interp: "Détails d'interprétation", int_langs: "Langue(s) à interpréter",
    int_format: "Format préféré", int_first_date: "Date approximative de la première mission (facultatif)",
    format_in_person: "En personne", format_opi: "Téléphone OPI", format_vri: "Vidéo VRI",
    section_transl: "Détails de traduction", src_lang: "Langue source", tgt_lang: "Langue cible",
    doc_type: "Type de document",
    doc_certificate: "Certificat", doc_medical: "Médical", doc_legal: "Juridique", doc_business: "Commercial", doc_other: "Autre",
    volume: "Taille approximative (par ex. 1500 mots ou 12 pages)",
    section_billing: "Facturation", billing_contact: "Contact de facturation (si différent)",
    billing_method: "Mode de facturation préféré", bm_invoice: "Facture (Net-15)", bm_card: "Carte de crédit", bm_po: "Bon de commande",
    po_number: "Numéro de PO",
    section_files: "Téléverser des fichiers (facultatif)",
    files_hint: "Jusqu'à 5 documents (max 25 Mo au total) — pièces originales, pièces d'identité, ordonnances, etc.",
    remove: "Retirer",
    section_notes: "Autre chose ?", notes_placeholder: "Ce que nous devrions savoir…",
    submit: "Envoyer", submitting: "Envoi en cours…",
    success_title: "Merci !", success_body: "Votre demande a bien été reçue. Notre équipe la traitera et vous contactera sous peu.",
    required: "requis", any: "N'importe",
  },
  ht: {
    title: "Fòm enskripsyon kliyan",
    subtitle: "Di nou sou bezwen entèpretasyon oswa tradiksyon w yo — sa pran anviwon 3 minit.",
    langLabel: "Lang fòm nan",
    section_basic: "Enfòmasyon w",
    name: "Òganizasyon / non w", contact: "Moun ki kontak", email: "Imèl", phone: "Telefòn", address: "Adrès",
    pref_lang: "Lang ou pi renmen pou kominikasyon",
    section_service: "Sèvis ou bezwen",
    service_type: "Ki sèvis ou bezwen?",
    interpretation: "Entèpretasyon (an pèsòn / telefòn / videyo)",
    translation: "Tradiksyon (dokiman)",
    both: "Tou de: entèpretasyon ak tradiksyon",
    org_type: "Kalite ka",
    section_interp: "Detay entèpretasyon", int_langs: "Lang pou entèprete",
    int_format: "Fòma preferans", int_first_date: "Dat premye misyon (opsyonèl)",
    format_in_person: "An pèsòn", format_opi: "Telefòn OPI", format_vri: "Videyo VRI",
    section_transl: "Detay tradiksyon", src_lang: "Lang orijin", tgt_lang: "Lang destinasyon",
    doc_type: "Kalite dokiman",
    doc_certificate: "Sètifika", doc_medical: "Medikal", doc_legal: "Legal", doc_business: "Biznis", doc_other: "Lòt",
    volume: "Gwosè apeprè (pa egzanp 1500 mo oswa 12 paj)",
    section_billing: "Faktirasyon", billing_contact: "Kontak pou fakti (si diferan)",
    billing_method: "Metòd faktirasyon ou prefere", bm_invoice: "Fakti (Net-15)", bm_card: "Kat kredi", bm_po: "Kòmand acha (PO)",
    po_number: "Nimewo PO",
    section_files: "Chaje dokiman (opsyonèl)",
    files_hint: "Jiska 5 dokiman (maks 25 MB) — dokiman orijinal, ID, ord tribunal, elatriye.",
    remove: "Retire",
    section_notes: "Yon lòt bagay?", notes_placeholder: "Nenpòt bagay nou ta dwe konnen…",
    submit: "Voye", submitting: "N ap voye…",
    success_title: "Mèsi!", success_body: "Nou resevwa enskripsyon w. Ekip nou an pral revize l epi kontakte w byento.",
    required: "obligatwa", any: "Nenpòt",
  },
};

export default function ClientIntake() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [lang, setLang] = useState("en");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({
    name: "", contact_name: "", email: "", phone: "", address: "",
    preferred_language: "en",
    service_type: "translation", org_type: "Other",
    interpretation_languages: "", interpretation_format: "in-person", first_assignment_date: "",
    source_language: "", target_language: "", document_type: "certificate", volume_estimate: "",
    billing_contact: "", billing_method: "invoice", po_number: "",
    notes: "",
  });
  const t = useMemo(() => I[lang] || I.en, [lang]);

  useEffect(() => {
    // If we have a token, load the invite to prefill.
    if (!token) return;
    axios.get(`${API}/public/client-intake/${token}`).then((r) => {
      setInvite(r.data);
      setLang(r.data.language || "en");
      setForm((f) => ({
        ...f,
        email: r.data.email || f.email,
        name: r.data.name || f.name,
        service_type: r.data.service_type || f.service_type,
        preferred_language: r.data.language || f.preferred_language,
      }));
      if (r.data.already_submitted) setDone(true);
    }).catch(() => {
      // token missing/revoked — silently fall back to open form
    });
  }, [token]);

  const handleFile = (e) => {
    const picked = Array.from(e.target.files || []);
    const combined = [...files, ...picked].slice(0, 5);
    setFiles(combined);
    e.target.value = "";
  };

  const submit = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.address.trim()) {
      return toast.error(t.required);
    }
    setSubmitting(true);
    const fd = new FormData();
    for (const [k, v] of Object.entries(form)) if (v !== null && v !== "") fd.append(k, v);
    fd.append("intake_language", lang);
    if (token) fd.append("invite_id", token);
    for (const f of files) fd.append("files", f);
    try {
      await axios.post(`${API}/public/client-intake`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="max-w-md w-full bg-white rounded-xl border border-slate-200 p-8 text-center shadow-sm" data-testid="intake-success">
          <CheckCircle size={48} weight="fill" className="mx-auto text-emerald-500 mb-4" />
          <h1 className="text-2xl font-bold text-slate-900">{t.success_title}</h1>
          <p className="text-slate-600 mt-2">{t.success_body}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="client-intake-page">
      <Toaster position="top-center" richColors />
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[#B91C1C] font-bold">Unity Language Group</div>
            <h1 className="text-3xl font-bold text-slate-900 mt-1">{t.title}</h1>
            <p className="text-slate-600 mt-1">{t.subtitle}</p>
          </div>
          <div className="min-w-[130px]">
            <Label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1">
              <Translate size={12} /> {t.langLabel}
            </Label>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger className="mt-1" data-testid="intake-lang"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="en">English</SelectItem>
                <SelectItem value="es">Español</SelectItem>
                <SelectItem value="fr">Français</SelectItem>
                <SelectItem value="ht">Kreyòl Ayisyen</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-6">
          {/* Basic */}
          <Section title={t.section_basic}>
            <F label={t.name} req>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="intake-name" />
            </F>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <F label={t.contact}><Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} /></F>
              <F label={t.email} req><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="intake-email" /></F>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <F label={t.phone} req><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="intake-phone" /></F>
              <F label={t.pref_lang}>
                <Select value={form.preferred_language} onValueChange={(v) => setForm({ ...form, preferred_language: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="fr">Français</SelectItem>
                    <SelectItem value="ht">Kreyòl Ayisyen</SelectItem>
                  </SelectContent>
                </Select>
              </F>
            </div>
            <F label={t.address} req><Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></F>
          </Section>

          {/* Service */}
          <Section title={t.section_service}>
            <F label={t.service_type}>
              <Select value={form.service_type} onValueChange={(v) => setForm({ ...form, service_type: v })}>
                <SelectTrigger data-testid="intake-service-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="interpretation">{t.interpretation}</SelectItem>
                  <SelectItem value="translation">{t.translation}</SelectItem>
                  <SelectItem value="both">{t.both}</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label={t.org_type}>
              <Select value={form.org_type} onValueChange={(v) => setForm({ ...form, org_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="USCIS">USCIS</SelectItem>
                  <SelectItem value="Medical">Medical</SelectItem>
                  <SelectItem value="Legal">Legal</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </Section>

          {/* Interpretation-specific */}
          {(form.service_type === "interpretation" || form.service_type === "both") && (
            <Section title={t.section_interp}>
              <F label={t.int_langs}><Input placeholder="e.g. French↔English, Haitian Creole↔English" value={form.interpretation_languages} onChange={(e) => setForm({ ...form, interpretation_languages: e.target.value })} /></F>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <F label={t.int_format}>
                  <Select value={form.interpretation_format} onValueChange={(v) => setForm({ ...form, interpretation_format: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in-person">{t.format_in_person}</SelectItem>
                      <SelectItem value="opi">{t.format_opi}</SelectItem>
                      <SelectItem value="vri">{t.format_vri}</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                <F label={t.int_first_date}><Input type="date" value={form.first_assignment_date} onChange={(e) => setForm({ ...form, first_assignment_date: e.target.value })} /></F>
              </div>
            </Section>
          )}

          {/* Translation-specific */}
          {(form.service_type === "translation" || form.service_type === "both") && (
            <Section title={t.section_transl}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <F label={t.src_lang}><Input value={form.source_language} onChange={(e) => setForm({ ...form, source_language: e.target.value })} /></F>
                <F label={t.tgt_lang}><Input value={form.target_language} onChange={(e) => setForm({ ...form, target_language: e.target.value })} /></F>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <F label={t.doc_type}>
                  <Select value={form.document_type} onValueChange={(v) => setForm({ ...form, document_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="certificate">{t.doc_certificate}</SelectItem>
                      <SelectItem value="medical">{t.doc_medical}</SelectItem>
                      <SelectItem value="legal">{t.doc_legal}</SelectItem>
                      <SelectItem value="business">{t.doc_business}</SelectItem>
                      <SelectItem value="other">{t.doc_other}</SelectItem>
                    </SelectContent>
                  </Select>
                </F>
                <F label={t.volume}><Input value={form.volume_estimate} onChange={(e) => setForm({ ...form, volume_estimate: e.target.value })} /></F>
              </div>
            </Section>
          )}

          {/* Billing */}
          <Section title={t.section_billing}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <F label={t.billing_contact}><Input value={form.billing_contact} onChange={(e) => setForm({ ...form, billing_contact: e.target.value })} /></F>
              <F label={t.billing_method}>
                <Select value={form.billing_method} onValueChange={(v) => setForm({ ...form, billing_method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="invoice">{t.bm_invoice}</SelectItem>
                    <SelectItem value="card">{t.bm_card}</SelectItem>
                    <SelectItem value="po">{t.bm_po}</SelectItem>
                  </SelectContent>
                </Select>
              </F>
            </div>
            {form.billing_method === "po" && (
              <F label={t.po_number}><Input value={form.po_number} onChange={(e) => setForm({ ...form, po_number: e.target.value })} /></F>
            )}
          </Section>

          {/* File uploads */}
          <Section title={t.section_files}>
            <p className="text-xs text-slate-500 -mt-2 mb-2">{t.files_hint}</p>
            <label className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-[#1E3A8A] transition text-sm text-slate-600" data-testid="intake-file-label">
              <Paperclip size={16} />
              <span>{files.length ? `${files.length} / 5` : "Choose files…"}</span>
              <input type="file" multiple className="hidden" onChange={handleFile} data-testid="intake-file-input" />
            </label>
            {files.length > 0 && (
              <ul className="mt-3 space-y-2">
                {files.map((f, i) => (
                  <li key={i} className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                    <span className="truncate mr-2 font-mono">{f.name} · {(f.size / 1024).toFixed(1)} KB</span>
                    <button type="button" onClick={() => setFiles(files.filter((_, x) => x !== i))} className="text-slate-400 hover:text-red-600" aria-label={t.remove}>
                      <X size={14} />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          {/* Notes */}
          <Section title={t.section_notes}>
            <Textarea rows={3} placeholder={t.notes_placeholder} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="intake-notes" />
          </Section>

          {invite?.note && (
            <div className="p-3 rounded-md bg-amber-50 border-l-4 border-amber-400 text-sm text-amber-800">
              <b>Message from Unity Language Group:</b><br/>{invite.note}
            </div>
          )}

          <Button
            onClick={submit}
            disabled={submitting}
            className="w-full h-12 text-base bg-[#1E3A8A] hover:bg-[#1E40AF] text-white"
            data-testid="intake-submit"
          >
            {submitting ? t.submitting : t.submit}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#B91C1C] font-bold mb-3">{title}</div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function F({ label, req, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
        {label} {req && <span className="text-red-500 normal-case">*</span>}
      </Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
